import { Popover, PopoverGroup, PopoverStateReturn } from 'components/Popover';
import { IconBell, IconBellV2, IconCloseSquare } from 'components/Icons';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { useModalActions } from 'components/ModalManager/Context';
import { Button } from 'components/ui_library/Button';
import { IconProps } from 'components/Icons/IconProps';
import { omit } from 'rambda';
import { ScrollWrapper } from 'components/Dialog/ScrollWrapper';
import { badgeStyles, notiticationPopoverStyles, emptyNotifications } from 'components/Navbar/Navbar.styles';
import { NotificationTemplate } from './Templates/NotificationTemplate';
import { INotification } from './interface';

const NOTIFICATIONS_MODAL_ID = 'notifications_modal_id';

interface BadgeIconProps extends React.PropsWithChildren<IconProps> {
  badgeProps?: BadgeProps;
}

type DesktopNotificationsPopoverProps = {
  notifications?: INotification[];
  popover: PopoverStateReturn;
};

export function BadgeIcon({ badgeProps, ...iconProps }: BadgeIconProps) {
  const WrappedBadgeValue = badgeProps?.badgeContent ? <p data-testid="notification-badge-count">{badgeProps?.badgeContent}</p> : null;

  const restBadgeProps = omit(['badgeContent'], badgeProps);
  const theme = useTheme();

  return (
    <ThemeProvider theme={theme}>
      <Badge color="error" overlap="circular" className={badgeStyles} badgeContent={WrappedBadgeValue} {...restBadgeProps}>
        <IconBell {...iconProps} />
      </Badge>
    </ThemeProvider>
  );
}

export function EmptyNotifications() {
  return (
    <div data-testid="empty-notifications" className={emptyNotifications}>
      <IconBellV2 size={112} />
      <h2>No new notifications.</h2>
      <p>Check back later.</p>
    </div>
  );
}

export function DesktopNotificationsPopover(props: DesktopNotificationsPopoverProps) {
  return (
    <Popover popover={props.popover} data-testid="notifications-popover" className={notiticationPopoverStyles}>
      <ScrollWrapper style={{ maxHeight: 500 }}>
        {props.notifications?.length ? (
          props.notifications.map((notification) => (
            <PopoverGroup key={notification.id}>
              <NotificationTemplate
                title={notification.title}
                createdDate={notification.createdDate}
                header={notification.header}
                body={notification.body}
                isLoading={notification.isLoading}
                hideIcon={notification.hideIcon}
                acceptBtn={notification.acceptBtn}
                declineBtn={notification.declineBtn}
                key={notification.id}
                type={notification.type}
              />
            </PopoverGroup>
          ))
        ) : (
          <EmptyNotifications />
        )}
      </ScrollWrapper>
    </Popover>
  );
}

export function MobileModalContent(props: { notifications?: INotification[] }) {
  const { closeModal } = useModalActions();

  return (
    <>
      <section className="heading">
        <Button variant="unstyled" onClick={() => closeModal({ id: NOTIFICATIONS_MODAL_ID })}>
          <IconCloseSquare size={24} />
        </Button>
      </section>

      <section className="body">
        <h1>Notifications</h1>
        {props.notifications?.length ? (
          props.notifications.map((notification) => (
            <NotificationTemplate
              title={notification.title}
              createdDate={notification.createdDate}
              header={notification.header}
              body={notification.body}
              isLoading={notification.isLoading}
              hideIcon={notification.hideIcon}
              acceptBtn={notification.acceptBtn}
              declineBtn={notification.declineBtn}
              key={notification.id}
              type={notification.type}
            />
          ))
        ) : (
          <EmptyNotifications />
        )}
      </section>
    </>
  );
}
